import { t } from "@travily/i18n";
import communityIllustration from "@ui/assets/illustrations/community-animated.webp";
import { Wizard } from "@ui/components/Wizard";
import { WizardTextField } from "@ui/components/WizardTextField";
import type { ReactNode } from "react";

const COLOR = "yellow" as const;

export interface CommunityNameStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Save and finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current value of the name field. */
  value: string;
  /** Field error for the name input, or undefined when valid. */
  error?: string;
  /** Called with the new value on every field change. */
  onChange: (value: string) => void;
  /** Called when the field loses focus (drives blur-time validation). */
  onBlur?: () => void;
}

export function CommunityNameStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  error,
  onChange,
  onBlur,
}: CommunityNameStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="h-32 w-auto" src={communityIllustration} />}
        heading={t("web.communities.creator.steps.name.show.title")}
        helper={t("web.communities.creator.steps.name.show.subtitle")}
      >
        <div className="w-full max-w-[600px]">
          <WizardTextField
            autoFocus
            color={COLOR}
            errorMessage={error}
            isInvalid={Boolean(error)}
            isLabelHidden
            label={t("web.accounts.setup.steps.name.label")}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !primaryPending) onPrimary();
            }}
            placeholder={t("web.communities.creator.steps.name.show.placeholder")}
            value={value}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
